import React, { useCallback } from 'react'

import * as Sentry from '@sentry/react'
import { Trans, t } from 'src/translations'

import {
  EmailMarketingPage,
  PageConfig,
  useEmailMarketingSignupMutation
} from 'src/apollo/sites'
import { ToastTOSAndPrivacy } from 'src/public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useSubmissionState } from 'shared/js/hooks/useSubmissionState'
import { AdditionalPagesType } from 'shared/js/types'

import { EditPathRoot } from 'public/components/default_template/main_page/MainPage'
import BetterTogetherPage from 'public/components/pages/better_together/BetterTogetherPage'

type Props = {
  content?: EmailMarketingPage
  editPathRoot?: EditPathRoot
  config?: PageConfig | null
}

export type EmailMarketingPageType = AdditionalPagesType[0]['content'] & {
  __typename: 'EmailMarketingPage'
}

const EmailSignupPage = (props: Props) => {
  const { restaurant } = useRestaurant()
  const [emailSignup] = useEmailMarketingSignupMutation()
  const { submitState, endSubmission, startSubmission, failSubmission } =
    useSubmissionState()
  const onSubmit = useCallback(
    async (data: any) => {
      startSubmission()
      try {
        if (restaurant.locations && restaurant.locations[0]?.externalId) {
          const resp = await emailSignup({
            variables: {
              email: data.email,
              restaurantGuid: `${restaurant.locations[0].externalId}`,
              source: 'WEBSITES'
            }
          })
          if (
            resp.data?.addEmailSubscriptionMutation?.status !== 'SUBSCRIBED'
          ) {
            Sentry.captureMessage(`ERROR: ${resp.errors?.[0]?.message}`)
            failSubmission()
          } else {
            endSubmission()
          }
        } else {
          failSubmission()
        }
      } catch (error) {
        failSubmission()
        Sentry.captureMessage(`ERROR: ${error}`)
      }
    },
    [
      emailSignup,
      restaurant.locations,
      endSubmission,
      failSubmission,
      startSubmission
    ]
  )

  return (
    <>
      <BetterTogetherPage
        content={props.content}
        editPathRoot={props.editPathRoot}
        config={props.config}
        onSubmit={onSubmit}
        useEmail={true}
        header={
          props.content?.headerText ||
          t('email-signup-page.sign-up-for-email-updates')
        }
        subheader={
          props.content?.subheaderText ||
          t('email-signup-page.stay-up-to-date', {
            restaurantName: restaurant.name
          })
        }
        isSubheaderEditable={true}
        successLine1={t('email-signup-page.thank-you-for-signing-up')}
        successLine2={t('email-signup-page.keep-you-updated', {
          restaurantName: restaurant.name
        })}
        errorLine1={t('email-signup-page.oh-no')}
        errorLine2={t('email-signup-page.trouble-submitting-request')}
        submitState={submitState}
        disclaimer={
          <Trans
            i18nKey='email-signup-page.disclaimer'
            components={{
              br: <br />,
              ToastTOSAndPrivacy: <ToastTOSAndPrivacy />
            }}
          />
        }
      />
    </>
  )
}

export default EmailSignupPage
